import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CloudUpload,
  Copy,
  Download,
  HardDriveDownload,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  Loading,
  Panel,
  PanelBody,
  SectionLabel,
  StatusPill,
} from '../ui/index.js';
import {
  deleteBackup,
  fetchBackupConfig,
  fetchBackups,
  runBackup,
  saveBackupConfig,
  testBackupConnection,
  verifyBackup,
  type BackupConfigDto,
  type BackupHealthDto,
  type BackupItemDto,
  type RestorePlanDto,
} from '../api.js';

/**
 * Sauvegarde & restauration — Cloudflare R2 (client request 2026-10-20).
 *
 * A native `pg_dump` is uploaded off-site to R2 with a tiered retention
 * (hourly / daily / weekly / monthly). This console configures the bucket,
 * shows the health of the series, lets the operator run a backup, verify an
 * archive and download it — and prepares the restore command. The restore
 * itself stays a human operation (docs/13 §4).
 */

const TIER_LABELS: Readonly<Record<string, string>> = {
  hourly: 'Horaire',
  daily: 'Quotidien',
  weekly: 'Hebdo',
  monthly: 'Mensuel',
  local: 'Volume local',
};

function mb(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

function ageLabel(iso: string): string {
  const hours = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000));
  if (hours < 1) return "à l'instant";
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'hier' : `il y a ${days} j`;
}

export function BackupConsole(): React.ReactElement {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [plan, setPlan] = useState<RestorePlanDto | null>(null);
  const [verified, setVerified] = useState<Record<string, { ok: boolean; detail: string }>>({});
  const [form, setForm] = useState({
    accountId: '',
    accessKeyId: '',
    secretAccessKey: '',
    bucket: '',
    prefix: 'mapara-backups',
    enabled: true,
    hourly: '24',
    daily: '14',
    weekly: '8',
    monthly: '12',
  });

  const listQuery = useQuery({
    queryKey: ['backups'],
    queryFn: fetchBackups,
    refetchInterval: 60_000,
  });

  const configQuery = useQuery({
    queryKey: ['backups-config'],
    queryFn: fetchBackupConfig,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['backups'] });
    void queryClient.invalidateQueries({ queryKey: ['backups-config'] });
  };

  const runMutation = useMutation({
    mutationFn: () => runBackup(),
    onSuccess: (res) => {
      setError(null);
      setMessage(
        `Sauvegarde ${res.file} créée (${mb(res.bytes)}) — ${res.uploaded.length} copie(s) envoyée(s) vers R2${res.skipped !== null ? ` — ${res.skipped}` : ''}.`,
      );
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const testMutation = useMutation({
    mutationFn: () => testBackupConnection(),
    onSuccess: (res) => {
      if (res.ok) {
        setError(null);
        setMessage(`Connexion R2 établie — ${res.detail}.`);
      } else {
        setMessage(null);
        setError(`Connexion R2 impossible : ${res.detail}`);
      }
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      saveBackupConfig({
        accountId: form.accountId,
        accessKeyId: form.accessKeyId,
        ...(form.secretAccessKey !== '' ? { secretAccessKey: form.secretAccessKey } : {}),
        bucket: form.bucket,
        prefix: form.prefix,
        enabled: form.enabled,
        retention: {
          hourly: Number(form.hourly) || 0,
          daily: Number(form.daily) || 0,
          weekly: Number(form.weekly) || 0,
          monthly: Number(form.monthly) || 0,
        },
      }),
    onSuccess: () => {
      setError(null);
      setMessage('Configuration R2 enregistrée (secret chiffré en base).');
      setShowConfig(false);
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteBackup(key),
    onSuccess: () => {
      setError(null);
      setDeleteKey(null);
      setMessage('Sauvegarde supprimée.');
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const items: readonly BackupItemDto[] = listQuery.data?.items ?? [];
  const health: BackupHealthDto | undefined = listQuery.data?.health;
  const config: BackupConfigDto | undefined = configQuery.data;
  const isConfigured = config?.configured === true;

  const openConfig = (): void => {
    setForm({
      accountId: config?.accountId ?? '',
      accessKeyId: config?.accessKeyId ?? '',
      secretAccessKey: '',
      bucket: config?.bucket ?? '',
      prefix: config?.prefix ?? 'mapara-backups',
      enabled: config?.enabled ?? true,
      hourly: String(config?.retention.hourly ?? 24),
      daily: String(config?.retention.daily ?? 14),
      weekly: String(config?.retention.weekly ?? 8),
      monthly: String(config?.retention.monthly ?? 12),
    });
    setShowConfig(true);
  };

  const healthTone = (): 'ok' | 'warn' | 'ko' | 'idle' => {
    if (health === undefined) return 'idle';
    if (health.status === 'ok') return 'ok';
    if (health.status === 'stale') return 'warn';
    if (health.status === 'not_configured') return 'warn';
    return 'warn';
  };

  return (
    <div className="space-y-4">
      {/* Feedback floats — it never pushes the content down */}
      {message !== null && (
        <div className="fixed right-5 top-20 z-50 max-w-md animate-rise" role="status">
          <Alert
            tone="good"
            title="Sauvegarde"
            actions={
              <button
                type="button"
                onClick={() => setMessage(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {message}
          </Alert>
        </div>
      )}
      {error !== null && (
        <div className="fixed right-5 top-20 z-50 max-w-md animate-rise" role="alert">
          <Alert
            tone="alarm"
            title="Échec"
            actions={
              <button
                type="button"
                onClick={() => setError(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {error}
          </Alert>
        </div>
      )}

      {/* ---------------- Health + actions ---------------- */}
      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={healthTone()}>
                  {health === undefined
                    ? 'lecture…'
                    : health.status === 'ok'
                      ? 'sauvegardes à jour'
                      : health.status === 'stale'
                        ? 'dernière sauvegarde trop ancienne'
                        : health.status === 'not_configured'
                          ? 'R2 non configuré'
                          : 'aucune sauvegarde'}
                </StatusPill>
                {config?.enabled === false && <Badge tone="muted">planification désactivée</Badge>}
                {config !== undefined && config.source === 'env' && (
                  <Badge tone="scan">configuration par variables d’environnement</Badge>
                )}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-ink-soft">
                {health?.lastBackupAt !== null && health?.lastBackupAt !== undefined ? (
                  <>
                    Dernière sauvegarde {ageLabel(health.lastBackupAt)} · {health.count} archive(s) sur R2 ·{' '}
                    {mb(health.totalBytes)} au total.
                  </>
                ) : (
                  <>Aucune sauvegarde sur R2 pour l’instant.</>
                )}{' '}
                Stratégie : <strong>horaire</strong> + copies quotidienne / hebdomadaire / mensuelle, chaque
                niveau purgé indépendamment.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()} disabled={listQuery.isFetching}>
                <RefreshCw className={`h-4 w-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
                Actualiser
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!isConfigured || testMutation.isPending}
                onClick={() => testMutation.mutate()}
              >
                <CloudUpload className="h-4 w-4" aria-hidden="true" />
                Tester la connexion
              </Button>
              <Button variant="soft" size="sm" onClick={openConfig}>
                <HardDriveDownload className="h-4 w-4" aria-hidden="true" />
                Configuration R2
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!isConfigured || runMutation.isPending}
                onClick={() => runMutation.mutate()}
                title="Crée une sauvegarde pg_dump et l’envoie sur Cloudflare R2"
              >
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                {runMutation.isPending ? 'Sauvegarde…' : 'Sauvegarder maintenant'}
              </Button>
            </div>
          </div>

          {listQuery.data?.r2Error != null && (
            <Alert tone="alarm" title="R2 injoignable">
              {listQuery.data.r2Error} — les archives locales restent listées.
            </Alert>
          )}
          {config !== undefined && !config.masterKeyPresent && (
            <Alert tone="warn" title="Clé maîtresse absente">
              Définissez <code>BACKUP_MASTER_KEY</code> (ou <code>PORTAL_CREDENTIAL_KEY</code>) sur le
              conteneur — 32 octets hex (<code>openssl rand -hex 32</code>) — sinon le secret R2 ne peut
              pas être chiffré en base.
            </Alert>
          )}
        </PanelBody>
      </Panel>

      {/* ---------------- Config form ---------------- */}
      {showConfig && (
        <Panel>
          <PanelBody className="space-y-3">
            <SectionLabel>Configuration Cloudflare R2</SectionLabel>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-xs font-bold text-ink">
                Account ID
                <Input
                  value={form.accountId}
                  onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                  placeholder="ex. 0123456789abcdef"
                  className="font-mono text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-ink">
                Access Key ID
                <Input
                  value={form.accessKeyId}
                  onChange={(e) => setForm({ ...form, accessKeyId: e.target.value })}
                  placeholder="ex. 8f2c…"
                  className="font-mono text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-ink">
                Secret Access Key
                <Input
                  type="password"
                  value={form.secretAccessKey}
                  onChange={(e) => setForm({ ...form, secretAccessKey: e.target.value })}
                  placeholder={config?.configured === true ? 'déjà enregistré — laisser vide pour conserver' : 'obligatoire'}
                  autoComplete="new-password"
                  className="font-mono text-sm"
                />
                <span className="text-[11px] font-normal text-ink-faint">
                  Chiffré en base (AES-256-GCM), jamais renvoyé par l’API.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-ink">
                Bucket
                <Input
                  value={form.bucket}
                  onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                  placeholder="ex. mapara-backups"
                  className="font-mono text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-ink">
                Préfixe des clés
                <Input
                  value={form.prefix}
                  onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                  placeholder="mapara-backups"
                  className="font-mono text-sm"
                />
              </label>
              <label className="flex items-center gap-2 pt-6 text-xs font-bold text-ink">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-border-strong accent-herb-600"
                />
                Planification horaire active
              </label>
            </div>

            <div>
              <SectionLabel>Rétention par niveau (nombre de copies conservées)</SectionLabel>
              <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(
                  [
                    { key: 'hourly', label: 'Horaire' },
                    { key: 'daily', label: 'Quotidien' },
                    { key: 'weekly', label: 'Hebdomadaire' },
                    { key: 'monthly', label: 'Mensuel' },
                  ] as const
                ).map((tier) => (
                  <label key={tier.key} className="flex flex-col gap-1 text-xs font-bold text-ink">
                    {tier.label}
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      value={form[tier.key]}
                      onChange={(e) => setForm({ ...form, [tier.key]: e.target.value })}
                      className="text-sm"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button variant="ghost" size="sm" onClick={() => setShowConfig(false)}>
                Annuler
              </Button>
              <Button variant="primary" size="sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer la configuration'}
              </Button>
            </div>
          </PanelBody>
        </Panel>
      )}

      {/* ---------------- Archive list ---------------- */}
      <Panel>
        <PanelBody className="space-y-3">
          <SectionLabel>Archives ({items.length})</SectionLabel>

          {listQuery.isLoading && <Loading label="Lecture des sauvegardes…" />}
          {listQuery.isError && (
            <Alert tone="alarm" title="Liste indisponible">
              {String(listQuery.error.message)}
            </Alert>
          )}

          {listQuery.data !== undefined && items.length === 0 && (
            <EmptyState
              icon={<CloudUpload className="h-6 w-6" aria-hidden="true" />}
              title="Aucune sauvegarde"
              hint="Configurez le bucket R2 puis cliquez « Sauvegarder maintenant » : l’archive partira hors du serveur, dans votre espace Cloudflare."
              action={
                <Button variant="primary" size="sm" onClick={openConfig}>
                  Configurer R2
                </Button>
              }
            />
          )}

          {items.length > 0 && (
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
              {items.map((item) => {
                const check = verified[item.key];
                return (
                  <li key={`${item.source}:${item.key}`} className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-card px-3 py-2.5 text-sm">
                    <Badge tone={item.tier === 'local' ? 'muted' : 'scan'}>
                      {TIER_LABELS[item.tier] ?? item.tier}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-xs font-semibold text-ink" title={item.key}>
                        {item.key}
                      </p>
                      <p className="text-[11px] text-ink-faint">
                        {new Date(item.createdAt).toLocaleString('fr-FR')} · {ageLabel(item.createdAt)} ·{' '}
                        {mb(item.bytes)}
                      </p>
                    </div>

                    {check !== undefined && (
                      <StatusPill tone={check.ok ? 'ok' : 'ko'} title={check.detail}>
                        {check.ok ? 'archive vérifiée' : 'vérification échouée'}
                      </StatusPill>
                    )}

                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                      {item.source === 'local' && (
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() =>
                            void verifyBackup(item.key).then((res) =>
                              setVerified((prev) => ({
                                ...prev,
                                [item.key]: {
                                  ok: res.ok,
                                  detail: res.ok
                                    ? `${res.tables} table(s), ${res.entries} entrée(s)`
                                    : (res.error ?? 'archive illisible'),
                                },
                              })),
                            )
                          }
                        >
                          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                          Vérifier
                        </Button>
                      )}
                      {item.source === 'r2' && (
                        <>
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => void fetchJsonRestorePlan(item.key).then(setPlan)}
                          >
                            <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Restaurer
                          </Button>
                          <a
                            href={`/api/v1/backups/${encodeURIComponent(item.key)}/download`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-xs font-semibold text-ink transition hover:border-border-strong hover:bg-cream"
                            title="Télécharger l’archive"
                          >
                            <Download className="h-3.5 w-3.5" aria-hidden="true" />
                            Télécharger
                          </a>
                        </>
                      )}
                      <Button
                        variant="danger"
                        size="xs"
                        onClick={() => setDeleteKey(item.key)}
                        title="Supprimer cette archive"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        Supprimer
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </PanelBody>
      </Panel>

      {/* ---------------- Restore assistant ---------------- */}
      {plan !== null && (
        <Panel>
          <PanelBody className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SectionLabel>Préparation de la restauration</SectionLabel>
                <p className="mt-1 text-sm font-semibold text-ink">{plan.key}</p>
              </div>
              <Button variant="ghost" size="xs" onClick={() => setPlan(null)}>
                Fermer
              </Button>
            </div>

            {plan.warnings.map((w) => (
              <Alert key={w} tone="warn" title="Attention">
                {w}
              </Alert>
            ))}

            <div>
              <SectionLabel>Commande (le mot de passe est masqué)</SectionLabel>
              <div className="mt-1.5 flex items-start gap-2">
                <code className="flex-1 overflow-x-auto rounded-lg border border-border bg-cream/60 px-3 py-2 font-mono text-[11px] text-ink">
                  {plan.command}
                </code>
                <Button
                  variant="soft"
                  size="sm"
                  onClick={() => void navigator.clipboard?.writeText(plan.command)}
                  title="Copier la commande"
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                  Copier
                </Button>
              </div>
            </div>

            <div>
              <SectionLabel>Exercice trimestriel (base jetable)</SectionLabel>
              <code className="mt-1.5 block overflow-x-auto rounded-lg border border-border bg-cream/60 px-3 py-2 font-mono text-[11px] text-ink">
                {plan.drillCommand}
              </code>
            </div>

            <div>
              <SectionLabel>Étapes à suivre</SectionLabel>
              <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-xs leading-relaxed text-ink-soft">
                {plan.checklist.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>

            <p className="flex items-start gap-2 border-t border-border pt-3 text-xs text-ink-soft">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay-600" aria-hidden="true" />
              <span>
                La console ne déclenche jamais la restauration : elle prépare la commande, un humain
                l’exécute (docs/13 §4). Un <code>.dump</code> contient des données sensibles — ne pas le
                déposer sur un partage non chiffré.
              </span>
            </p>
          </PanelBody>
        </Panel>
      )}

      <ConfirmDialog
        open={deleteKey !== null}
        tone="alarm"
        title="Supprimer la sauvegarde"
        confirmLabel="Supprimer définitivement"
        busy={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteKey !== null) deleteMutation.mutate(deleteKey);
        }}
        onCancel={() => setDeleteKey(null)}
      >
        <p className="leading-relaxed text-sm text-ink">
          Supprimer <strong>{deleteKey}</strong> ? Cette suppression est irréversible.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/** Small helper: the restore plan is fetched lazily when the operator asks. */
async function fetchJsonRestorePlan(key: string): Promise<RestorePlanDto> {
  const res = await fetch(`/api/v1/backups/${encodeURIComponent(key)}/restore-plan`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as RestorePlanDto;
}
